import { useEffect, useRef } from "react";
import type { TraceTarget } from "@/domain/trace";

export type TraceRibbonEntry = { target: TraceTarget; label: string; kindLabel?: string; amount?: string; edge?: string };

const KIND_LABEL: Record<TraceTarget["kind"], string> = {
  disbursement: "Disbursement",
  milestone: "Milestone",
  project: "Project",
  subProject: "Sub-project",
  spend: "Spend ask",
};

// Text/background pairing is keyed off ONE variant per node, not two
// independently-computed isRoot/isCurrent booleans — a node that is BOTH
// root and current (a freshly-opened single-node trace) previously mixed
// the "current" background (bg-brandSoft, light) with the "root" text color
// (text-onDark, white-on-white-ish), rendering the name illegible. Current
// wins the visual variant when both apply; the "Root" fact still shows in
// the type label regardless.
type Variant = "current" | "root" | "default";

const VARIANT_CLASS: Record<Variant, string> = {
  current: "border-brand bg-brandSoft shadow-[0_0_0_3px_var(--accent-line)]",
  root: "border-ink bg-ink",
  default: "border-line bg-card hover:border-strong hover:bg-subtle",
};

const TYPE_LABEL_CLASS: Record<Variant, string> = {
  current: "font-semibold text-brand",
  root: "text-white/60",
  default: "text-faint",
};

const NAME_CLASS: Record<Variant, string> = {
  current: "text-ink",
  root: "text-onDark",
  default: "text-ink",
};

const META_CLASS: Record<Variant, string> = {
  current: "text-faint",
  root: "text-white/60",
  default: "text-faint",
};

/**
 * The full walked path, no truncation (Trace Source spec §3.3) — replaces
 * the old Breadcrumb, which collapsed to first/…/second-to-last/last above
 * 4 crumbs. Auto-scrolls the current node into view on every walk so a deep
 * path (6+) always keeps the current node visible without the user having
 * to scroll manually (spec §10.1).
 */
export function TracePathRibbon({ entries, onWalk }: { entries: TraceRibbonEntry[]; onWalk: (index: number) => void }) {
  const currentRef = useRef<HTMLButtonElement>(null);
  const currentKey = entries.length ? `${entries[entries.length - 1].target.kind}:${entries[entries.length - 1].target.id}` : "";

  useEffect(() => {
    currentRef.current?.scrollIntoView({ block: "nearest", inline: "center", behavior: "auto" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey]);

  return (
    <div>
      <p className="text-meta text-faint">Path</p>
      <nav
        aria-label="Trace path"
        className="mt-2 flex gap-0 overflow-x-auto pb-1.5"
        style={{ maskImage: "linear-gradient(to right, transparent, black 12px, black calc(100% - 12px), transparent)" }}
      >
        {entries.map((entry, i) => {
          const isRoot = i === 0;
          const isCurrent = i === entries.length - 1;
          const variant: Variant = isCurrent ? "current" : isRoot ? "root" : "default";
          const kindLabel = entry.kindLabel ?? KIND_LABEL[entry.target.kind];
          const typeLabel = [isRoot ? "Root" : null, kindLabel, isCurrent ? "here" : null].filter(Boolean).join(" · ");
          return (
            <div key={`${entry.target.kind}:${entry.target.id}`} className="flex shrink-0 items-stretch">
              {i > 0 && (
                <div className="flex shrink-0 items-center gap-1 px-1.5 text-faint">
                  <span aria-hidden className="text-sm text-strong">▸</span>
                  {entry.edge && (
                    <span className="rounded border border-line bg-card px-1.5 py-0.5 font-mono text-[9.5px] text-faint">{entry.edge}</span>
                  )}
                </div>
              )}
              <button
                ref={isCurrent ? currentRef : undefined}
                type="button"
                aria-current={isCurrent ? "step" : undefined}
                onClick={() => onWalk(i)}
                title={entry.label}
                className={
                  "flex min-w-[140px] max-w-[180px] shrink-0 flex-col gap-0.5 rounded-lg border px-2.5 py-2 text-left transition-colors " +
                  VARIANT_CLASS[variant]
                }
              >
                <span className={"font-mono text-[9px] uppercase tracking-wide " + TYPE_LABEL_CLASS[variant]}>{typeLabel}</span>
                <span className={"truncate text-[12.5px] font-medium " + NAME_CLASS[variant]}>{entry.label}</span>
                {entry.amount && <span className={"font-mono text-[10px] " + META_CLASS[variant]}>{entry.amount}</span>}
              </button>
            </div>
          );
        })}
      </nav>
    </div>
  );
}
